import { parseSSE } from "@/lib/api/locationLive";
import { useAuthStore } from "@/stores/auth-store";

/**
 * GET /auth/session/events: the server says "ended" the moment this tab's
 * session is ended anywhere (signed out from another device, by an
 * administrator, a password change, the idle timeout), and "features" when a
 * Super Admin switches one of the company's modules on or off.
 *
 * fetch() and a stream reader rather than EventSource, because EventSource
 * cannot send the Authorization header and the token must never go in a URL.
 * The server closes each stream after about 100 seconds and this reconnects,
 * so a refreshed access token is picked up on the next connection.
 */
export function openSessionEndedStream(handlers: {
  /** The session has ended. */
  onEnded: () => void;
  /** The stream was refused with 401: the access token may only need a refresh. */
  onUnauthorized: () => void;
  /** The company's modules were switched on or off: reload the features. */
  onFeaturesChanged?: () => void;
}): () => void {
  const controller = new AbortController();
  let stopped = false;
  let delay = 2000;

  const run = async () => {
    while (!stopped) {
      try {
        const { accessToken, impersonatedTenant } = useAuthStore.getState();
        if (!accessToken) return;
        const headers: Record<string, string> = { Accept: "text/event-stream", Authorization: `Bearer ${accessToken}` };
        if (impersonatedTenant) headers["X-Tenant-ID"] = impersonatedTenant.id;
        const res = await fetch(`${import.meta.env.VITE_API_BASE_URL}/auth/session/events`, {
          headers,
          signal: controller.signal,
          cache: "no-store",
        });
        if (res.status === 401) {
          handlers.onUnauthorized();
          throw new Error("unauthorized");
        }
        // An older server without the endpoint: nothing to listen to.
        if (res.status === 404 || res.status === 501) return;
        if (!res.ok || !res.body) throw new Error(`stream status ${res.status}`);

        delay = 2000;
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        for (;;) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parsed = parseSSE(buffer);
          buffer = parsed.rest;
          if (parsed.messages.some((m) => m.event === "features")) handlers.onFeaturesChanged?.();
          if (parsed.messages.some((m) => m.event === "ended")) {
            stopped = true;
            handlers.onEnded();
            return;
          }
        }
      } catch {
        if (stopped || controller.signal.aborted) return;
      }
      if (stopped) return;
      await new Promise((resolve) => setTimeout(resolve, delay));
      delay = Math.min(delay * 2, 30000);
    }
  };
  void run();

  return () => {
    stopped = true;
    controller.abort();
  };
}
